import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Activity, TrendingUp, BarChart3 } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1da668c2-f525-4bfb-96ff-4bebb59788b6/images/38894f5cc8ef08943f851f5c9195f585d496021b038b80aed855753b23616f30.png";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isRegister = loc.pathname === "/register";
  return <AuthShell isRegister={isRegister} onSuccess={() => nav("/")} />;
}

export function Register() {
  const nav = useNavigate();
  return <AuthShell isRegister onSuccess={() => nav("/")} />;
}

function AuthShell({ isRegister, onSuccess }) {
  const { login, register } = useAuth();
  const [email, setEmail] = useState(isRegister ? "" : "admin@trademonitor.com");
  const [password, setPassword] = useState(isRegister ? "" : "admin123");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = isRegister
      ? await register(email, password, name)
      : await login(email, password);
    setBusy(false);
    if (res.ok) onSuccess();
    else setErr(res.error || "Failed");
  }

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-[#08080A]" data-testid="auth-page">
      {/* Left: hero image */}
      <div className="hidden lg:block relative overflow-hidden border-r border-[#27272A]">
        <img src={HERO_IMG} alt="Trading" className="h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#08080A]/60 via-transparent to-[#08080A]/80" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="flex items-center gap-2 text-[#A1A1AA] text-xs tracking-[0.3em] uppercase mb-4">
            <Activity className="h-3 w-3" /> Live · Indian Markets
          </div>
          <h2 className="text-4xl font-semibold text-white tracking-tight leading-tight max-w-md">
            Monitor every trade with surgical precision.
          </h2>
          <p className="mt-4 text-sm text-[#A1A1AA] max-w-md">
            Multi-broker share trading journal — live NSE prices, risk per trade,
            realized P&amp;L and full audit history.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-8 w-8 bg-[#007AFF] flex items-center justify-center rounded-sm">
              <BarChart3 className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-semibold tracking-[0.25em] uppercase text-white">Trade Monitor</span>
          </div>

          <div className="text-xs tracking-[0.25em] uppercase text-[#71717A] mb-2">
            {isRegister ? "Create Account" : "Sign In"}
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight mb-8">
            {isRegister ? "Start tracking trades." : "Welcome back."}
          </h1>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {isRegister && (
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 bg-[#121318] border-[#27272A] text-white rounded-sm h-11"
                  placeholder="Your name"
                />
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#121318] border-[#27272A] text-white rounded-sm h-11"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#121318] border-[#27272A] text-white rounded-sm h-11"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div data-testid="auth-error" className="text-xs text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 px-3 py-2 rounded-sm">
                {err}
              </div>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm h-11 font-medium tracking-wide"
            >
              {busy ? "Please wait…" : isRegister ? "Create Account" : "Sign In"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-[#A1A1AA]">
            {isRegister ? (
              <>Already have an account?{" "}
                <Link to="/login" className="text-[#007AFF] hover:underline" data-testid="link-to-login">Sign in</Link>
              </>
            ) : (
              <>New here?{" "}
                <Link to="/register" className="text-[#007AFF] hover:underline" data-testid="link-to-register">Create an account</Link>
              </>
            )}
          </div>

          {!isRegister && (
            <div className="mt-8 text-xs text-[#71717A] border-t border-[#27272A] pt-4">
              <span className="tracking-[0.2em] uppercase">Demo</span> · admin@trademonitor.com / admin123
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
